import { ApiProblem, type Api } from '@cdn/api';
import { Observable } from '@cdn/state';

/** The part of a browser window a visit needs; tests supply their own */
export interface Opened {
	closed: boolean;
	opener: unknown;
	location: { replace(url: string): void };
	close(): void;
}

interface Specs {
	api: Api;
	application: string;
	/** Origin of published delivery, for a ticket link that is origin-relative; empty when not configured */
	delivery: string;
	/** Opens a blank window inside the click that asked for it, so popup blockers allow it */
	open?: () => Opened | null;
}

/**
 * A member's visit to a private application. `access.tickets.create` confirms the membership and answers a
 * one-time ticket whose link carries it in the URL fragment to the application's own host, which exchanges it
 * for its host-only cookie. While the window stays open the visit asks again within the returned `window`, so
 * the confirmation of membership never lapses; the tickets of those renewals are dropped unread.
 *
 * The ticket is a secret: it lives in a local variable for the moment it takes to hand the link to the new
 * window, and is never stored, logged, rendered or kept on this object. The opened window loses its `opener`
 * before it navigates, because tenant content is untrusted and must not reach the console.
 */
export class Visit extends Observable {
	#api: Api;
	#application: string;
	#delivery: string;
	#open: () => Opened | null;
	#window: Opened | undefined;
	#timer: ReturnType<typeof setTimeout> | undefined;

	#state: 'idle' | 'opening' | 'open' | 'ended' = 'idle';
	/** `ended` once the window was closed or the membership could no longer be confirmed */
	get state() {
		return this.#state;
	}

	#problem: unknown;
	get problem() {
		return this.#problem;
	}

	#blocked = false;
	/** true when the browser refused to open a window */
	get blocked() {
		return this.#blocked;
	}

	#unreachable = false;
	/** true when the application has no domain and no delivery origin is configured to complete the link */
	get unreachable() {
		return this.#unreachable;
	}

	constructor({ api, application, delivery, open }: Specs) {
		super();
		this.#api = api;
		this.#application = application;
		this.#delivery = delivery;
		this.#open = open ?? (() => window.open('about:blank', '_blank') as Opened | null);
	}

	/**
	 * Call from the click itself: the window has to open before the ticket is requested
	 */
	async start() {
		if (this.#state === 'opening' || this.#state === 'open') return;
		this.#problem = undefined;
		this.#unreachable = false;
		const opened = this.#open();
		this.#blocked = !opened;
		if (!opened) return void this.changed();

		opened.opener = null;
		this.#window = opened;
		this.#move('opening');
		try {
			const ticket = await this.#api.call('access.tickets.create', { params: { application: this.#application } });
			const link = this.#link(ticket.url);
			if (!link) {
				this.#unreachable = true;
				return this.#end();
			}
			opened.location.replace(link);
			this.#move('open');
			this.#schedule(ticket.window);
		} catch (problem) {
			this.#problem = problem;
			this.#end();
		}
	}

	destroy() {
		clearTimeout(this.#timer);
		this.#window = undefined;
	}

	// Only an http(s) link that carries its secret in the fragment is followed
	#link(url: string): string | undefined {
		const absolute = /^https?:\/\//.test(url) ? url : this.#delivery && url.startsWith('/') ? `${this.#delivery}${url}` : undefined;
		if (!absolute) return;
		try {
			const parsed = new URL(absolute);
			return /^https?:$/.test(parsed.protocol) && parsed.hash.startsWith('#ticket=') && !parsed.search.includes('ticket') ? parsed.href : undefined;
		} catch {
			return undefined;
		}
	}

	#move(state: Visit['state']) {
		this.#state = state;
		this.changed();
	}

	#end() {
		clearTimeout(this.#timer);
		this.#state === 'opening' && this.#window?.close();
		this.#window = undefined;
		this.#move('ended');
	}

	// Two thirds of the window leaves room for a slow answer before the confirmation lapses
	#schedule(span: number) {
		clearTimeout(this.#timer);
		this.#timer = setTimeout(() => void this.#renew(), Math.max(1000, Math.floor((span * 2) / 3)));
	}

	async #renew() {
		if (!this.#window || this.#window.closed) return this.#end();
		try {
			const { window: span } = await this.#api.call('access.tickets.create', { params: { application: this.#application } });
			this.#window && this.#schedule(span);
		} catch (problem) {
			// A refusal means the membership ended or the application changed: access lapses, nothing to keep alive
			if (ApiProblem.of(problem).is('UNAUTHENTICATED', 'FORBIDDEN', 'NOT_FOUND', 'STATE_INVALID', 'ACCESS_REVOKED')) {
				this.#problem = problem;
				return this.#end();
			}
			this.#window && this.#schedule(15000);
		}
	}
}
